// src/pages/NotFound.jsx
import React, { useEffect, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Home } from "lucide-react";


const normLang = (x) => ((x || "").toLowerCase() === "en" ? "en" : "sq");

const t = {
  en: {
    title: "Page not found",
    subtitle: "That link doesn’t exist (or moved). Let’s get you back home.",
    cta: "Go home",
  },
  sq: {
    title: "Faqja nuk u gjet",
    subtitle: "Ky link nuk ekziston (ose është zhvendosur). Kthehu te faqja kryesore.",
    cta: "Shko në fillim",
  },
};

export default function NotFound() {
  const loc = useLocation();
  const [sp] = useSearchParams();

  const langFromPath = useMemo(() => {
    const seg = (loc.pathname || "/").split("/").filter(Boolean)[0];
    return seg === "en" ? "en" : seg === "sq" ? "sq" : "";
  }, [loc.pathname]);

  const lang = normLang(sp.get("lang") || langFromPath || "sq");
  const tr = t[lang];

  useEffect(() => {
    document.title = `404 • ${tr.title} | MikDok`;
  }, [tr.title]);

  return (
    <div className="nf-wrap">
        
      <div className="nf-card" role="main" aria-label="Not Found">
        {/* animated blob */}
        <div className="nf-blob" aria-hidden="true" />

        {/* animated 404 mark */}
        <div className="nf-mark" aria-hidden="true">
          <span>4</span>
          <span className="nf-zero">0</span>
          <span>4</span>
        </div>

        {/* avatar */}
        <div className="nf-avatar" aria-hidden="true">
          <img
            src="/img/notfound-nurse.jpg"
            alt=""
            loading="lazy"
            decoding="async"
          />
        </div>


        <h1 className="nf-title">{tr.title}</h1>
        <p className="nf-sub">{tr.subtitle}</p>

        <Link className="nf-cta" to={`/?lang=${lang}`}>
          <Home size={18} />
          <span>{tr.cta}</span>
        </Link>

        <div className="nf-mini">
          MikDok • {lang === "sq" ? "Rezervo në sekonda" : "Book in seconds"}
        </div>
      </div>

      <style>{`
        .nf-wrap{
          min-height: 100vh;
          display: grid;
          place-items: center;
          padding: 24px;
          background:
            radial-gradient(900px 500px at 20% 10%, rgba(59,130,246,.20), transparent 60%),
            radial-gradient(700px 420px at 90% 15%, rgba(99,102,241,.18), transparent 55%),
            linear-gradient(180deg, #ffffff 0%, #f7f8fb 70%);
          color: #0b0f18;
        }

        .nf-card{
          width: min(720px, 100%);
          border-radius: 22px;
          background: rgba(255,255,255,.82);
          border: 1px solid rgba(15,23,42,.10);
          box-shadow: 0 18px 70px rgba(17,24,39,.10);
          padding: 22px;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(10px);
        }

        .nf-blob{
          position: absolute;
          inset: -80px -80px auto auto;
          width: 240px;
          height: 240px;
          border-radius: 999px;
          background: linear-gradient(135deg, rgba(59,130,246,.35), rgba(99,102,241,.35));
          filter: blur(2px);
          transform: rotate(15deg);
          animation: nf-float 6.5s ease-in-out infinite;
          pointer-events: none;
        }

        .nf-mark{
          display: flex;
          gap: 10px;
          align-items: baseline;
          justify-content: center;
          font-weight: 1000;
          letter-spacing: -0.06em;
          margin-top: 10px;
          user-select: none;
        }

        .nf-mark span{
          font-size: 84px;
          line-height: 1;
        }

        .nf-zero{
          position: relative;
          width: 78px;
          height: 78px;
          display: inline-grid;
          place-items: center;
          border-radius: 26px;
          color: #fff;
          background: linear-gradient(135deg, rgba(59,130,246,1), rgba(99,102,241,1));
          box-shadow: 0 16px 44px rgba(59,130,246,.25);
          transform-origin: center;
          animation: nf-wobble 2.8s ease-in-out infinite;
        }

        .nf-title{
          margin: 14px 0 6px;
          text-align: center;
          font-size: 28px;
          line-height: 1.1;
          letter-spacing: -0.03em;
          font-weight: 1000;
        }

        .nf-sub{
          margin: 0 auto 16px;
          max-width: 520px;
          text-align: center;
          font-size: 14px;
          font-weight: 800;
          color: rgba(11,15,24,.62);
        }

        .nf-cta{
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 16px;
          text-decoration: none;
          font-weight: 1000;
          color: #fff;
          background: linear-gradient(135deg, rgba(59,130,246,1), rgba(99,102,241,1));
          box-shadow: 0 16px 44px rgba(59,130,246,.24);
          border: none;
          margin: 0 auto;
        }

        .nf-cta:hover{
          transform: translateY(-1px);
        }

        .nf-mini{
          margin-top: 14px;
          text-align: center;
          font-size: 12px;
          font-weight: 900;
          color: rgba(11,15,24,.52);
        }
                  .nf-avatar{
          width: 94px;
          height: 94px;
          border-radius: 999px;
          margin: 8px auto 10px;
          background: rgba(255,255,255,.95);
          border: 4px solid rgba(255,255,255,.95);
          box-shadow:
            0 18px 40px rgba(17,24,39,.14),
            0 0 0 1px rgba(15,23,42,.08);
          overflow: hidden;
        }

        .nf-avatar img{
          width: 100%;
          height: 100%;
          object-fit: cover;
          /* THIS is what makes it show the face */
          object-position: center 15%;
          display: block;
        }



        @keyframes nf-float{
          0%, 100% { transform: translate3d(0,0,0) rotate(15deg); }
          50% { transform: translate3d(-10px, 14px, 0) rotate(12deg); }
        }

        @keyframes nf-wobble{
          0%, 100% { transform: rotate(0deg) translateY(0); }
          25% { transform: rotate(-4deg) translateY(-1px); }
          50% { transform: rotate(4deg) translateY(1px); }
          75% { transform: rotate(-2deg) translateY(0); }
        }

        /* Mobile tweaks */
        @media (max-width: 520px){
          .nf-card{ padding: 18px; }
          .nf-mark span{ font-size: 68px; }
          .nf-zero{ width: 66px; height: 66px; border-radius: 22px; }
          .nf-title{ font-size: 24px; }
        }

        /* Reduce motion accessibility */
        @media (prefers-reduced-motion: reduce){
          .nf-blob, .nf-zero { animation: none !important; }
          .nf-cta:hover{ transform: none; }
        }
      `}</style>
    </div>
  );
}
